import { Alert, Box, Typography } from '@mui/material'
import { t } from 'i18next'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { CancelButton } from '../../../../components/CancelButton'
import BasicDatePicker from '../../../../components/DateTimePicker'
import { AppForm, FormSubmitButton, FormTextField } from '../../../../components/FormCore'
import { useZodForm } from '../../../../hooks/zod-form'
import { getFormattedDate } from '../../../../utils'
import {
  addDays,
  earliestOf,
  editableFrom,
  isWritableDay,
  latestOf,
  startOfDay,
  toApiDate,
} from '../../../localities/tasks/vesselSuccession'

const EditTaskResourceForm = ({
  taskResource,
  predecessor,
  successor,
  workOrderStart,
  workOrderEnd,
  allowDescription = false,
  onSubmit,
  pop,
}) => {
  const { t: translate } = useTranslation(['locality', 'common', 'task'])

  const startWritable = isWritableDay(taskResource?.startDate)
  const endWritable = isWritableDay(taskResource?.endDate)

  const workOrderFrom = useMemo(() => (workOrderStart ? startOfDay(workOrderStart) : undefined), [workOrderStart])
  const workOrderTo = useMemo(() => (workOrderEnd ? startOfDay(workOrderEnd) : undefined), [workOrderEnd])

  const earliestStart = useMemo(
    () =>
      latestOf(predecessor?.startDate ? addDays(predecessor.startDate, 1) : undefined, workOrderFrom, editableFrom()),
    [predecessor, workOrderFrom],
  )

  const latestEnd = useMemo(
    () => earliestOf(successor?.endDate ? addDays(successor.endDate, -1) : undefined, workOrderTo),
    [successor, workOrderTo],
  )

  const schema = z
    .object({
      startDate: z.string().or(z.date()),
      endDate: z.string().or(z.date()),
      description: z.string().max(400).optional().nullable(),
    })
    .refine(values => values.startDate != null && values.startDate !== '', {
      path: ['startDate'],
      message: t('required', { ns: 'common' }),
    })
    .refine(values => values.endDate != null && values.endDate !== '', {
      path: ['endDate'],
      message: t('required', { ns: 'common' }),
    })
    .refine(values => startOfDay(values.startDate) <= startOfDay(values.endDate), {
      message: t('endStartDateError', { ns: 'common' }),
      path: ['endDate'],
    })
    .refine(values => !startWritable || !earliestStart || earliestStart <= startOfDay(values.startDate), {
      message: t('must-be-date-or-later', { ns: 'task', date: getFormattedDate(earliestStart) }),
      path: ['startDate'],
    })
    .refine(values => !endWritable || !latestEnd || latestEnd >= startOfDay(values.endDate), {
      message: t('must-be-date-or-earlier', { ns: 'task', date: getFormattedDate(latestEnd) }),
      path: ['endDate'],
    })
    .refine(values => !endWritable || editableFrom() <= startOfDay(values.endDate), {
      message: t('must-be-date-or-later', { ns: 'task', date: getFormattedDate(editableFrom()) }),
      path: ['endDate'],
    })

  const form = useZodForm(schema, {
    defaultValues: {
      startDate: taskResource?.startDate ? toApiDate(taskResource.startDate).toISOString() : undefined,
      endDate: taskResource?.endDate ? toApiDate(taskResource.endDate).toISOString() : undefined,
      description: taskResource?.description ?? '',
    },
  })

  const watchedStart = form.watch('startDate')
  const watchedEnd = form.watch('endDate')

  const earliestEnd = useMemo(
    () => latestOf(watchedStart ? startOfDay(watchedStart) : undefined, workOrderFrom, editableFrom()),
    [watchedStart, workOrderFrom],
  )

  const latestStart = useMemo(
    () => earliestOf(watchedEnd ? startOfDay(watchedEnd) : undefined, workOrderTo),
    [watchedEnd, workOrderTo],
  )

  const movedPredecessorEnd = useMemo(() => {
    if (!predecessor?.endDate || !watchedStart) return null
    const shifted = addDays(watchedStart, -1)
    return startOfDay(predecessor.endDate).getTime() === shifted.getTime() ? null : shifted
  }, [predecessor, watchedStart])

  const movedSuccessorStart = useMemo(() => {
    if (!successor?.startDate || !watchedEnd) return null
    const shifted = addDays(watchedEnd, 1)
    return startOfDay(successor.startDate).getTime() === shifted.getTime() ? null : shifted
  }, [successor, watchedEnd])

  const onSubmitHandle = useCallback(
    async formValues => {
      await onSubmit(formValues)
      pop()
    },
    [onSubmit, pop],
  )

  return (
    <AppForm form={form} onSubmit={onSubmitHandle} hasInitialValues={true}>
      <Box pb={2}>
        <Typography variant="caption" color="text.secondary">
          {translate('resource', { ns: 'task' })}
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {taskResource?.resource?.name ?? taskResource?.resourceName ?? ''}
        </Typography>
      </Box>
      <BasicDatePicker
        label={translate('startDate', { ns: 'common' })}
        name="startDate"
        disabled={!startWritable}
        minDate={earliestStart && toApiDate(earliestStart)}
        maxDate={latestStart && toApiDate(latestStart)}
        onChange={date => {
          form.setValue('startDate', date)
          form.trigger(['startDate', 'endDate'])
        }}
      />
      <BasicDatePicker
        label={translate('endDate', { ns: 'common' })}
        name="endDate"
        disabled={!endWritable}
        minDate={earliestEnd && toApiDate(earliestEnd)}
        maxDate={latestEnd && toApiDate(latestEnd)}
        onChange={date => {
          form.setValue('endDate', date)
          form.trigger(['startDate', 'endDate'])
        }}
      />
      {!startWritable && !endWritable && (
        <Box pb={2}>
          <Typography variant="caption" color="text.secondary">
            {translate('vessel-dates-settled', { ns: 'task' })}
          </Typography>
        </Box>
      )}
      {movedPredecessorEnd && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('replaced-vessel-will-end-on', {
            ns: 'task',
            vessel: predecessor?.resource?.name ?? predecessor?.resourceName ?? '',
            date: getFormattedDate(movedPredecessorEnd),
          })}
        </Alert>
      )}
      {movedSuccessorStart && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('replacing-vessel-will-start-on', {
            ns: 'task',
            vessel: successor?.resource?.name ?? successor?.resourceName ?? '',
            date: getFormattedDate(movedSuccessorStart),
          })}
        </Alert>
      )}
      {allowDescription && (
        <FormTextField rows={5} multiline name="description" label={translate('description', { ns: 'common' })} />
      )}
      <Box className="flex justify-end gap-4 pt-4">
        <CancelButton onClick={pop}>{translate('cancel', { ns: 'common' })}</CancelButton>
        <FormSubmitButton createText={translate('save', { ns: 'common' })} />
      </Box>
    </AppForm>
  )
}

export default EditTaskResourceForm
